import type { JSX } from 'react';
import { useTranslation } from '../../shared/i18n';
import { useErrorText } from '../../shared/i18n/useErrorText';
import {
  CREDENTIAL_KINDS_BY_SERVICE,
  type CredentialKind,
  type CredentialService,
} from '@netdeck/contracts';
import {
  useAttachCredential,
  useCredentials,
  useDetachCredential,
  useNodeCredentials,
} from './credentials';

/**
 * Привязка учётных данных к службе узла.
 *
 * Пароль здесь не вводится и не показывается: заводится он один раз
 * на экране «Учётные данные», а к машинам привязывается ссылкой. В парке,
 * где на всех машинах один пароль VNC, обратный порядок означал бы сотню
 * копий одного секрета — и сотню мест, где его менять.
 *
 * Служба задаётся снаружи: пароль от VNC и пароль от камеры хранятся
 * и привязываются одинаково (§7.6), и второй такой же компонент разошёлся бы
 * с первым при первой же правке.
 *
 * Подходящие виды берутся из каталога по службе, а не задаются рядом
 * с ней: у SSH их два — пароль и приватный ключ, — и держать соответствие
 * в двух местах значит однажды показать в списке пароль там, где нужен
 * ключ. Явный `kinds` остаётся на случай, когда службе нужна часть видов.
 */
/** Короткая подпись вида — ключами словаря, для списка, где видов несколько. */
const KIND_HINT_KEYS: Readonly<Record<CredentialKind, string>> = {
  vnc: 'credentials.hint.password',
  rtsp: 'credentials.hint.password',
  snmp: 'credentials.hint.community',
  http_basic: 'credentials.hint.password',
  ssh: 'credentials.hint.password',
  ssh_key: 'credentials.hint.privateKey',
};

export function NodeCredentials({
  nodeId,
  service = 'vnc',
  kinds,
  emptyHint,
}: {
  nodeId: string;
  service?: CredentialService;
  /** Чем можно закрыть эту службу. По умолчанию — всё, что ей подходит. */
  kinds?: readonly CredentialKind[];
  /**
   * Что сказать, когда учётных данных нужного вида ещё не завели.
   *
   * Ключ словаря, а не текст: подпись собирается здесь, на языке зрителя,
   * а значение по умолчанию иначе вычислялось бы один раз при загрузке
   * модуля и не менялось бы при переключении языка.
   */
  emptyHint?: string;
}): JSX.Element {
  const { t } = useTranslation();
  const errorText = useErrorText();

  const all = useCredentials();
  const attached = useNodeCredentials(nodeId);
  const attach = useAttachCredential(nodeId);
  const detach = useDetachCredential(nodeId);

  const allowed = kinds ?? CREDENTIAL_KINDS_BY_SERVICE[service];
  const suitable = (all.data ?? []).filter((credential) => allowed.includes(credential.kind));
  const current = attached.data?.[service] ?? null;

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs text-dim" htmlFor={`credential-${service}-${nodeId}`}>
        {t('credentials.fieldLabel')}
      </label>

      <div className="flex items-center gap-2">
        <select
          id={`credential-${service}-${nodeId}`}
          value={current?.id ?? ''}
          onChange={(event) => {
            const credentialId = event.target.value;

            if (credentialId === '') {
              detach.mutate(service);
              return;
            }

            attach.mutate({ service, credentialId });
          }}
          className="h-8 min-w-0 flex-1 rounded-xs border border-line bg-bg px-2 text-sm"
        >
          <option value="">{t('credentials.none')}</option>
          {suitable.map((credential) => (
            <option key={credential.id} value={credential.id}>
              {/*
                Вид дописывается, только когда службу закрывают несколько
                видов: у SSH пароль и приватный ключ лежат в одном списке,
                и выбрать не тот — это отказ входа, который потом ищут
                на сервере. Там, где вид один, подпись была бы шумом.
              */}
              {allowed.length > 1
                ? `${credential.name} — ${t(KIND_HINT_KEYS[credential.kind])}`
                : credential.name}
            </option>
          ))}
        </select>
      </div>

      {suitable.length === 0 ? (
        <p className="text-xs text-dim">{t(emptyHint ?? 'credentials.noVncHint')}</p>
      ) : null}

      {attach.error ? <p className="text-xs text-down">{errorText(attach.error)}</p> : null}
    </div>
  );
}
